import React, { useState, useEffect, useCallback } from 'react';
import { getTiposProducto, createTipoProducto, updateTipoProducto, deleteTipoProducto } from '../../services/muestrasApi';
import { useMuestrasPermissions } from '../../components/muestras/LayoutMuestras';
import { toast } from 'sonner';
import { Tag, Plus, Edit2, Trash2, X, GripVertical, AlertTriangle } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SortableRow = ({ item, canEdit, canDelete, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id_tipo });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const showActions = canEdit || canDelete;

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50 border-b">
      {canEdit && (
        <td className="px-3 py-3 w-10">
          <button {...attributes} {...listeners} className="cursor-grab text-gray-400 hover:text-gray-600">
            <GripVertical className="w-4 h-4" />
          </button>
        </td>
      )}
      <td className="px-4 py-3 font-mono text-sm text-gray-600 font-medium">{item.id_tipo}</td>
      <td className="px-4 py-3 font-medium text-gray-800">{item.nombre_tipo || '-'}</td>
      {showActions && (
        <td className="px-4 py-3">
          <div className="flex justify-center gap-1">
            {canEdit && (
              <button 
                onClick={() => onEdit(item)} 
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                data-testid={`edit-tipo-${item.id_tipo}`}
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
            {canDelete && (
              <button 
                onClick={() => onDelete(item)} 
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid={`delete-tipo-${item.id_tipo}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

const MuestrasTipos = ({ muestrasUser, muestrasIsAdmin }) => {
  // Usar el nuevo sistema de permisos
  const { canCreate, canEdit, canDelete, isAdmin } = useMuestrasPermissions();
  
  // Permisos específicos para Tipos
  const puedeCrear = canCreate('tipos');
  const puedeEditar = canEdit('tipos');
  const puedeEliminar = canDelete('tipos');
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ nombre_tipo: '' });
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try { 
      const tipos = await getTiposProducto();
      setData((tipos || []).sort((a, b) => (a.orden || 0) - (b.orden || 0)));
    } 
    catch (error) { toast.error('Error cargando tipos'); } 
    finally { setLoading(false); }
  };

  const openModal = useCallback((item = null) => {
    setEditingItem(item);
    setFormData({ nombre_tipo: item?.nombre_tipo || '' });
    setShowModal(true);
  }, []);

  const handleSubmit = async () => {
    if (!formData.nombre_tipo) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingItem) {
        await updateTipoProducto(editingItem.id_tipo, formData);
        toast.success('Tipo actualizado');
      } else {
        await createTipoProducto(formData);
        toast.success('Tipo creado');
      }
      setShowModal(false);
      loadData();
    } catch (error) { toast.error(error.message); }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try { 
      await deleteTipoProducto(deleteItem.id_tipo); 
      toast.success('Tipo eliminado'); 
      setDeleteItem(null);
      loadData(); 
    } catch (error) { 
      toast.error(error.message); 
    } finally {
      setDeleting(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex(item => item.id_tipo === active.id);
    const newIndex = data.findIndex(item => item.id_tipo === over.id);
    const newData = arrayMove(data, oldIndex, newIndex);
    setData(newData);

    try {
      const token = localStorage.getItem('token');
      const items = newData.map((item, idx) => ({ id_tipo: item.id_tipo, orden: idx }));
      await fetch(`${API_BASE}/api/muestras/tipos-producto/reorder`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items })
      });
      toast.success('Orden actualizado');
    } catch (error) {
      toast.error('Error al guardar orden');
      loadData();
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-tipos-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-green-100 rounded-xl">
            <Tag className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Tipos de Producto</h1>
            <p className="text-gray-400 text-sm">Gestión del catálogo de tipos {puedeEditar && '• Arrastra para reordenar'}</p>
          </div>
        </div>
        {puedeCrear && (
          <button onClick={() => openModal()} className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium">
            <Plus className="w-4 h-4" />
            Agregar
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : data.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay tipos registrados</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {puedeEditar && <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-10"></th>}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-20">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">NOMBRE</th>
                  {(puedeEditar || puedeEliminar) && <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-28">ACCIONES</th>}
                </tr>
              </thead>
              <SortableContext items={data.map(item => item.id_tipo)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {data.map(item => (
                    <SortableRow key={item.id_tipo} item={item} canEdit={puedeEditar} canDelete={puedeEliminar} onEdit={openModal} onDelete={setDeleteItem} />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        )}
      </div>

      {/* Modal de Crear/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{editingItem ? 'Editar Tipo' : 'Nuevo Tipo'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
              <input
                type="text"
                value={formData.nombre_tipo}
                onChange={(e) => setFormData({ ...formData, nombre_tipo: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Ej: Pantalón"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">
                Cancelar
              </button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium">
                {editingItem ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar Tipo?</h3>
              <p className="text-gray-500 mb-6">
                ¿Estás seguro de eliminar <strong>"{deleteItem.nombre_tipo}"</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={() => setDeleteItem(null)} 
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmDelete} 
                  className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50"
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuestrasTipos;
